import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { FormulaItemTableRow } from '@/tools/formula/FormulaItemTableRow.molecule';
import { OUTPUT_SECTION_BORDER_STYLES } from '@/tools/models/template.types';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { BadgeVariant } from '@seeqdev/qomponents';

interface FormulaErrors {
  message: string;
  column: number;
  line: number;
}

interface FormulaWithParameters {
  formula: string;
  parameters: { name: string; identifier: string; item?: { id?: string } }[];
  formulaErrors?: FormulaErrors[];
}

export interface FormulaItemTableOption {
  key: string;
  display: string;
  allFormulas?: FormulaWithParameters[];
  id?: string;
  isNew?: boolean;
  showNewLabel?: boolean;
  isMapped?: boolean;
  mappedFeatureId?: string;
  /** Wire-format item type from the source derived feature (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /**
   * Wire-format metadata map from the source derived feature. `undefined` means the feature has no
   * restriction; the metadata annotation renders "Any" in that state.
   */
  outputItemMetadata?: Record<string, string>;
}

/** A single badge to render for an option: which visual variant, and its already-translated text. */
export type OutputBadgeEntry = {
  variant: BadgeVariant;
  text: string;
};

export interface FormulaItemTableIF {
  options: FormulaItemTableOption[];
  testId?: string;
  extraClassNames?: string;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  hasErrors?: boolean;
  newLabel?: string;
  mappingConfig?: FeatureMappingConfig;
  /** Renders a checkbox column before each row's name when true. */
  selectable?: boolean;
  /** Keys of options whose checkbox is checked. Only meaningful when `selectable` is true. */
  selectedKeys?: Set<string>;
  /** Fires with the option's key and the checkbox's new checked state. */
  onSelectionChange?: (key: string, selected: boolean) => void;
  /** Called when the Select All checkbox is toggled. Falls back to per-option `onSelectionChange` calls if omitted. */
  onSelectAllChange?: (selected: boolean) => void;
  /**
   * Per-option badges to render, in order. An option absent from the map renders no badges at all. Callers
   * own deciding variant/text (e.g. "required complete" -> success badge) -- this atom only knows how to
   * render a given variant/text pair, not how to derive one from domain data.
   */
  outputBadges?: Map<string, OutputBadgeEntry[]>;
}

export interface FeatureMappingConfig {
  label: string;
  labelTooltip?: string;
  mappingPlaceholder?: string;
  mappingOptions: ReactSelectOption[];
  onMappingChange?: (optionKey: string, isMapped: boolean, mappedFeatureId?: string) => void;
}

export const FormulaItemTable = ({
  extraClassNames,
  options,
  testId,
  onEditClick,
  onRemoveClick,
  onAdditionalIconClick,
  additionalIcon,
  additionalIconTooltip,
  onSecondaryIconClick,
  secondaryIcon,
  secondaryIconTooltip,
  onTextChange,
  textEditable,
  hasErrors = false,
  newLabel,
  mappingConfig,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  onSelectAllChange,
  outputBadges,
}: FormulaItemTableIF) => {
  const { t } = useTranslation();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Expand only the rows that actually have a formula error, once per new `options` value while hasErrors is true
  const [lastErrorExpandedOptions, setLastErrorExpandedOptions] = useState<FormulaItemTableOption[] | null>(null);
  if (hasErrors && lastErrorExpandedOptions !== options) {
    setLastErrorExpandedOptions(options);
    const keysWithErrors = options
      .filter((opt) => opt.allFormulas?.some((formula) => (formula.formulaErrors?.length ?? 0) > 0))
      .map((opt) => opt.key);
    setExpandedKeys(new Set(keysWithErrors));
  }

  const toggleRow = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allKeys = options.map((option) => option.key);
  const allSelected = allKeys.length > 0 && allKeys.every((key) => selectedKeys?.has(key));

  return (
    <table
      className={clsx('table table-condensed mb0 width-100percent', extraClassNames)}
      data-testid={testId}
      tabIndex={0}>
      <tbody>
        {selectable && (
          <tr
            className={clsx(
              OUTPUT_SECTION_BORDER_STYLES,
              'tw:border-t tw:bg-sq-table-highlight tw:dark:bg-sq-table-highlight-dark',
            )}>
            <td className="forceZeroPadding">
              <div className="tw:px-[5px] tw:py-[5px]">
                <Checkbox
                  id={`${testId ?? 'formulaItemTable'}SelectAllCheckbox`}
                  isChecked={allSelected}
                  onChange={(e) => {
                    if (onSelectAllChange) {
                      onSelectAllChange(e.target.checked);
                    } else {
                      allKeys.forEach((key) => onSelectionChange?.(key, e.target.checked));
                    }
                  }}
                />
              </div>
            </td>
            <td colSpan={1} className="forceZeroPadding">
              <div className="tw:relative tw:py-[5px]">
                <label
                  htmlFor={`${testId ?? 'formulaItemTable'}SelectAllCheckbox`}
                  className="text-bold tw:cursor-pointer">
                  {allSelected ? t('UNSELECT_ALL') : t('SELECT_ALL')}
                </label>
              </div>
            </td>
          </tr>
        )}
        {options.map((option) => (
          <FormulaItemTableRow
            key={option.key}
            option={option}
            testId={testId}
            isOpen={expandedKeys.has(option.key)}
            onToggleFormula={() => toggleRow(option.key)}
            onEditClick={onEditClick}
            onRemoveClick={onRemoveClick}
            onAdditionalIconClick={onAdditionalIconClick}
            additionalIcon={additionalIcon}
            additionalIconTooltip={additionalIconTooltip}
            onSecondaryIconClick={onSecondaryIconClick}
            secondaryIcon={secondaryIcon}
            secondaryIconTooltip={secondaryIconTooltip}
            textEditable={textEditable}
            onTextChange={onTextChange}
            newLabel={newLabel}
            mappingConfig={mappingConfig}
            selectable={selectable}
            isSelected={selectedKeys?.has(option.key) ?? false}
            onSelectionChange={onSelectionChange}
            badges={outputBadges?.get(option.key)}
          />
        ))}
      </tbody>
    </table>
  );
};
